import {
  isFerryIngredientResponseWithStaticInfo,
  isFlightIngredientResponseWithStaticInfo,
} from '@libs/search-service-smart-search';
import type {
  AvailableFerryIngredientResponseWithStaticInfo,
  AvailableFlightIngredientResponseWithStaticInfo,
  AvailableTransportIngredientResponseWithStaticInfo,
  ExposedFerryIngredient,
  ExposedFlightIngredient,
  ExposedHotelIngredient,
  ExposedIngredient,
  ExposedTransportIngredient,
  FerryIngredientResponseWithStaticInfo,
  FlightIngredientResponseWithStaticInfo,
  HotelIngredientResponseWithStaticInfo,
  HotelIngredientAdapter,
  OldExposedFlightIngredient,
  OldExposedHotelIngredient,
  OldExposedIngredient,
  TransportIngredientAdapter,
  OwnStayIngredientAdapter,
  OwnStayIngredientResponseWithStaticInfo,
  ExposedOwnStayIngredient,
  OldIngredientType,
  OwnTransportIngredientAdapter,
  ExposedOwnTransportIngredient,
} from '@libs/trip-planner-interfaces';
import { IngredientType, TransportType } from '@libs/trip-planner-interfaces';

import type { ActivityIngredient } from './activityIngredient';
import type { FerryIngredient } from './ferryIngredient';
import type { FlightIngredient } from './flightIngredient';
import type { HotelIngredient } from './hotelIngredient';
import type { OwnStayIngredient } from './ownStayIngredient';
import type { OwnTransportIngredient } from './ownTransportIngredient';

export type TransportIngredient = FlightIngredient | FerryIngredient;
export type TransportIngredientType =
  | IngredientType.Flight
  | IngredientType.Ferry;

export type StayIngredient = OwnStayIngredient | HotelIngredient;

export type RecipeMainIngredient =
  | StayIngredient
  | TransportIngredient
  | OwnTransportIngredient;

export type RecipeIngredient = RecipeMainIngredient | ActivityIngredient;

export function isStayIngredient(
  ingredient: RecipeIngredient
): ingredient is StayIngredient {
  return isOwnStayIngredient(ingredient) || isHotelIngredient(ingredient);
}

export function isOwnStayIngredient(
  ingredient: RecipeIngredient
): ingredient is OwnStayIngredient {
  return ingredient.ingredientType === IngredientType.OwnStay;
}

export function isOwnTransportIngredient(
  ingredient: RecipeIngredient
): ingredient is OwnTransportIngredient {
  return ingredient.ingredientType === IngredientType.OwnTransport;
}

export function isHotelIngredient(
  ingredient: RecipeIngredient
): ingredient is HotelIngredient {
  return ingredient.ingredientType === IngredientType.Hotel;
}

export function isTransportIngredient(
  ingredient: RecipeIngredient
): ingredient is TransportIngredient {
  return isFlightIngredient(ingredient) || isFerryIngredient(ingredient);
}

export function isFlightIngredient(
  ingredient: RecipeIngredient
): ingredient is FlightIngredient {
  return ingredient.ingredientType === IngredientType.Flight;
}

export function isFerryIngredient(
  ingredient: RecipeIngredient
): ingredient is FerryIngredient {
  return ingredient.ingredientType === IngredientType.Ferry;
}

export function isActivityIngredient(
  ingredient: RecipeIngredient
): ingredient is ActivityIngredient {
  return ingredient.ingredientType === IngredientType.Activity;
}

export function isExposedHotelIngredient(
  ingredient: ExposedIngredient | OldExposedIngredient
): ingredient is ExposedHotelIngredient | OldExposedHotelIngredient {
  return isSameIngredientType(ingredient.ingredientType, IngredientType.Hotel);
}

export function isExposedOwnStayIngredient(
  ingredient: ExposedIngredient | OldExposedIngredient
): ingredient is ExposedOwnStayIngredient {
  return isSameIngredientType(
    ingredient.ingredientType,
    IngredientType.OwnStay
  );
}

export function isExposedOwnTransportIngredient(
  ingredient: ExposedIngredient | OldExposedIngredient
): ingredient is ExposedOwnTransportIngredient {
  return isSameIngredientType(
    ingredient.ingredientType,
    IngredientType.OwnTransport
  );
}

export function isExposedTransportIngredient(
  ingredient: ExposedIngredient | OldExposedIngredient
): ingredient is ExposedTransportIngredient {
  return isSameIngredientType(
    ingredient.ingredientType,
    IngredientType.Transport
  );
}

export function isExposedFlightTransportIngredient(
  ingredient: ExposedIngredient | OldExposedIngredient
): ingredient is
  | ExposedFlightIngredient
  | OldExposedFlightIngredient
  | ExposedTransportIngredient {
  const flightIngredientTypeMatches = isSameIngredientType(
    ingredient.ingredientType,
    IngredientType.Flight
  );

  const transportIngredientTypeMatches =
    isExposedTransportIngredient(ingredient) &&
    !!ingredient.transportTypes[0] &&
    isSameIngredientType(ingredient.transportTypes[0], TransportType.Flight);

  return flightIngredientTypeMatches || transportIngredientTypeMatches;
}

export function isExposedFerryTransportIngredient(
  ingredient: ExposedIngredient | OldExposedIngredient
): ingredient is ExposedFerryIngredient | ExposedTransportIngredient {
  const ferryIngredientTypeMatches = isSameIngredientType(
    ingredient.ingredientType,
    IngredientType.Ferry
  );

  const transportIngredientTypeMatches =
    isExposedTransportIngredient(ingredient) &&
    !!ingredient.transportTypes[0] &&
    isSameIngredientType(ingredient.transportTypes[0], TransportType.Ferry);

  return ferryIngredientTypeMatches || transportIngredientTypeMatches;
}

function isSameIngredientType(
  firstIngredientType: IngredientType | OldIngredientType | TransportType,
  secondIngredientType: IngredientType | OldIngredientType | TransportType
): boolean {
  return (
    firstIngredientType.toLocaleLowerCase().replace('_', '') ===
    secondIngredientType.toLocaleLowerCase().replace('_', '')
  );
}

export function isAvailableFlightTransportIngredientResponseWithStaticInfo(
  ingredient:
    | OwnStayIngredientResponseWithStaticInfo
    | HotelIngredientResponseWithStaticInfo
    | FlightIngredientResponseWithStaticInfo
    | FerryIngredientResponseWithStaticInfo
): ingredient is AvailableFlightIngredientResponseWithStaticInfo {
  return (
    isFlightIngredientResponseWithStaticInfo(ingredient) &&
    ingredient.isAvailable
  );
}
export function isAvailableFerryTransportIngredientResponseWithStaticInfo(
  ingredient:
    | OwnStayIngredientResponseWithStaticInfo
    | HotelIngredientResponseWithStaticInfo
    | FlightIngredientResponseWithStaticInfo
    | FerryIngredientResponseWithStaticInfo
): ingredient is AvailableFerryIngredientResponseWithStaticInfo {
  return (
    isFerryIngredientResponseWithStaticInfo(ingredient) &&
    ingredient.isAvailable
  );
}
export function isAvailableTransportIngredientResponseWithStaticInfo(
  ingredient:
    | OwnStayIngredientResponseWithStaticInfo
    | HotelIngredientResponseWithStaticInfo
    | FlightIngredientResponseWithStaticInfo
    | FerryIngredientResponseWithStaticInfo
): ingredient is AvailableTransportIngredientResponseWithStaticInfo {
  return (
    isAvailableFlightTransportIngredientResponseWithStaticInfo(ingredient) ||
    isAvailableFerryTransportIngredientResponseWithStaticInfo(ingredient)
  );
}

export function isOwnStayIngredientAdapter(
  ingredientAdapter:
    | OwnStayIngredientAdapter
    | HotelIngredientAdapter
    | TransportIngredientAdapter
    | OwnTransportIngredientAdapter
): ingredientAdapter is OwnStayIngredientAdapter {
  return ingredientAdapter.ingredientType === IngredientType.OwnStay;
}

export function isHotelIngredientAdapter(
  ingredientAdapter:
    | OwnStayIngredientAdapter
    | HotelIngredientAdapter
    | TransportIngredientAdapter
    | OwnTransportIngredientAdapter
): ingredientAdapter is HotelIngredientAdapter {
  return ingredientAdapter.ingredientType === IngredientType.Hotel;
}
